import { useMemo } from 'react';

import { useUsageMutation } from '@shared/apis/usageApi';
import {
  EventChangeEvents,
  EventChangePayloads,
  EventProperties,
  UsageApiSlug,
} from '@shared/types/common/eventTracking';

// Event tracking is fire-and-forget: never awaited, never surfaced into a user flow. Failures
// are logged and dropped, and a disabled tracker is a no-op.

export interface EventTrackingConfig {
  // The tracked area, which is also the endpoint the events are posted to. Absent when a
  // runtime-resolved caller has no area: nowhere to post, so nothing fires.
  apiSlug?: UsageApiSlug;
  // Absent means enabled: only an explicit false disables the tracker.
  enabled?: boolean;
  // Prepended to every event name on the wire (`${eventPrefix}_${event}`), for callers whose area
  // is decided at runtime and whose map is therefore keyed by suffix.
  eventPrefix?: string;
}

// fire takes an event and the payload that event declares; fireChange takes only the change events
// and their payload, and drops the event when the change turns out to be a no-op.
export interface EventTracker<Payloads extends Record<string, EventProperties>> {
  fire: <Ev extends keyof Payloads & string>(event: Ev, properties: Payloads[Ev]) => void;
  fireChange: (event: EventChangeEvents<Payloads>, properties: EventChangePayloads<Payloads>) => void;
}

// Name the area's map alongside its slug —
// useEventTracking<AvailabilityScheduleEventPayloads>({ apiSlug: 'availability-schedule' }).
// Nothing checks the two belong to the same area. Payloads defaults to never, so omitting the map
// gives a tracker that rejects every call rather than one that is silently loose.
export const useEventTracking = <Payloads extends Record<string, EventProperties> = never>(
  config: EventTrackingConfig
) => {
  // mutate is a stable reference in react-query 4, so depending on it directly is safe. The empty
  // fallback is never posted to: both callbacks return early without a slug.
  const { mutate } = useUsageMutation(config.apiSlug ?? '');

  // Keyed on the config's fields, not the object: callers pass inline literals, so keying on
  // identity would rebuild the tracker every render.
  return useMemo(() => {
    const post = (event: string, properties: EventProperties) => {
      if (config.enabled === false || !config.apiSlug) return;

      const wireEvent = config.eventPrefix ? `${config.eventPrefix}_${event}` : event;

      mutate(
        { body: { event: wireEvent, properties } },
        {
          onError: (error) => {
            console.error(`Failed to track event ${wireEvent}:`, error);
          },
        }
      );
    };

    return {
      fire: <Ev extends keyof Payloads & string>(event: Ev, properties: Payloads[Ev]) => post(event, properties),
      // Nothing is emitted when the value did not actually move, so a no-op change cannot be logged.
      fireChange: (event: EventChangeEvents<Payloads>, properties: EventChangePayloads<Payloads>) => {
        if (properties.change_from === properties.change_to) return;

        post(event, properties);
      },
    } satisfies EventTracker<Payloads>;
  }, [config.apiSlug, config.enabled, config.eventPrefix, mutate]);
};
